import { useCallback, useEffect, useRef, useState } from 'react';
import { useQuery, useQueryClient, type UseQueryResult } from '@tanstack/react-query';
import type {
  MemoryCreateBody, MemoryDTO, MemoryListDTO, MemoryNoticeAckBody, MemoryNoticeClaimDTO, MemoryNoticeDTO, MemoryNoticeDoneDTO, MemoryProposeDTO,
} from '@contracts/assistant-memory';
import type { Id } from '@contracts/common';
import { api, ApiClientError } from '../../../api/client';

/**
 * Hana's memory on the web (S3 §9.7). The notes page reads under `MEMORIES_KEY`; the "Em đã nhớ" notices are not a
 * query at all: a claim is a write (it hands out a lease and a delivery token), sent once per panel open and never
 * again on a refetch or an invalidation of `['assistant']` (D191, D192).
 */

export const MEMORIES_KEY = ['assistant', 'memories'] as const;
const NOTICES = '/api/assistant/memory-notices';
const MEMORIES = '/api/assistant/memories';

/** The memory page's read (§9.7, D195): the owner's own, also while Hana is off or read-only. */
export function useMemories(): UseQueryResult<MemoryListDTO, ApiClientError> {
  return useQuery<MemoryListDTO, ApiClientError>({ queryKey: MEMORIES_KEY, queryFn: () => api<MemoryListDTO>(MEMORIES) });
}
const memoryPath = (id: Id) => `${MEMORIES}/${encodeURIComponent(id)}`;
export const createMemory = (body: MemoryCreateBody): Promise<MemoryDTO> =>
  api<MemoryDTO>(MEMORIES, { method: 'POST', body, requestId: crypto.randomUUID() });
/** D174: a text edit or a pin, at the note's version (409 when it moved on). */
export const patchMemory = (id: Id, body: { version: number; text?: string; pinned?: boolean }): Promise<MemoryDTO> =>
  api<MemoryDTO>(memoryPath(id), { method: 'PATCH', body, requestId: crypto.randomUUID() });
export const deleteMemory = (m: Pick<MemoryDTO, 'id' | 'version'>): Promise<{ id: Id }> =>
  api<{ id: Id }>(`${memoryPath(m.id)}?version=${m.version}`, { method: 'DELETE', requestId: crypto.randomUUID() });
export const forgetAllMemories = (): Promise<{ forgotten: number }> =>
  api<{ forgotten: number }>(`${MEMORIES}/forget-all`, { method: 'POST', body: {}, requestId: crypto.randomUUID() });

export const claimMemoryNotices = (): Promise<MemoryNoticeClaimDTO> =>
  api<MemoryNoticeClaimDTO>(`${NOTICES}/claim`, { method: 'POST', body: {} });
const deliveryOf = (n: MemoryNoticeDTO): MemoryNoticeAckBody =>
  ({ deliveryToken: n.deliveryToken, memoryVersion: n.memoryVersion });
export const ackMemoryNotice = (n: MemoryNoticeDTO): Promise<MemoryNoticeDoneDTO> =>
  api<MemoryNoticeDoneDTO>(`${NOTICES}/${n.id}/ack`, { method: 'POST', body: deliveryOf(n) });
export const discardMemoryNotice = (n: MemoryNoticeDTO): Promise<MemoryNoticeDoneDTO> =>
  api<MemoryNoticeDoneDTO>(`${NOTICES}/${n.id}/discard`, { method: 'POST', body: deliveryOf(n) });
/** D189: no request id — the proposal leaves no trace of who sent it; a retry at the same version is a no-op. */
export const proposeMemory = (memoryId: Id, version: number): Promise<MemoryProposeDTO> =>
  api<MemoryProposeDTO>(`/api/assistant/memories/${memoryId}/propose-company`, { method: 'POST', body: { version } });

/** The answers that say the note or its delivery is no longer this line's: it leaves the line. */
export const isGone = (e: unknown): boolean =>
  e instanceof ApiClientError && (e.status === 404 || e.status === 409 || e.status === 410);

export type MemoryNotices = {
  notices: readonly MemoryNoticeDTO[];
  /** The line is on screen: every note not yet acknowledged is, once. */
  painted: (shown: readonly MemoryNoticeDTO[]) => void;
  /** The note leaves the line (discarded, or gone on the server). */
  drop: (id: Id) => void;
};

/**
 * The notices of one panel open (D192): claimed the first time the chat view shows — the panel unmounts when it
 * closes, so the next open claims again. A refused or failed claim shows nothing and says nothing. The ack is sent by
 * `painted`, which the line calls from its effect after it rendered: a claim whose line never reached the screen is
 * never acknowledged, and its lease runs out for the next open.
 */
export function useMemoryNotices(chat: boolean): MemoryNotices {
  const queryClient = useQueryClient();
  const [notices, setNotices] = useState<readonly MemoryNoticeDTO[]>([]);
  const claimed = useRef(false);
  const acked = useRef(new Set<Id>());
  useEffect(() => {
    if (!chat || claimed.current) return;
    claimed.current = true;
    claimMemoryNotices().then((answer) => setNotices(answer.notices), () => undefined);
  }, [chat]);
  const drop = useCallback((id: Id) => setNotices((list) => list.filter((n) => n.id !== id)), []);
  const painted = useCallback((shown: readonly MemoryNoticeDTO[]) => {
    for (const n of shown) {
      if (acked.current.has(n.id)) continue;
      acked.current.add(n.id);
      // An acknowledged note becomes one Hana may use: the notes page shows it as such.
      ackMemoryNotice(n).then(() => queryClient.invalidateQueries({ queryKey: MEMORIES_KEY }),
        (e: unknown) => { if (isGone(e)) drop(n.id); });
    }
  }, [drop, queryClient]);
  return { notices, painted, drop };
}
